import { StripeProduct } from "@kvix/shared";
import { Button as KvixButton, KvixIcon } from "@kvix/ui";
import {
  Button,
  Grid,
  makeStyles,
  Menu,
  MenuItem,
  Theme,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, RouteChildrenProps, Switch, useLocation } from "react-router";
import { Link, NavLink, Redirect, useHistory } from "react-router-dom";
import styled, { css } from "styled-components";
import { ExpoContext } from "../../..//contexts/expo";
import {
  hasRequiredProduct,
  isInstructor,
  KvixUserContext,
} from "../../../contexts/user";
import { ProfilePageAccount } from "./pages/Account";
import { ProfilePageBilling } from "./pages/Billing";
import { ProfilePageDashboard } from "./pages/Dashboard";
import { ProfilePageFavourites } from "./pages/Favourites";
import { ProfilePageRecruitments } from "./pages/Recruitments";

const useStyles = makeStyles((theme) => ({
  select: {
    color: theme.palette.common.white,
    width: "100%",

    "& .MuiSelect-root": {
      backgroundColor: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  tabContainer: {
    margin: 0,
    width: "auto",
  },
  tabButton: {
    display: "flex",
    alignItems: "center",
    "&:hover": {
      "& span": {
        backgroundColor: "rgba(250, 37, 94, 0.08)",
      },
    },
  },
  tabContent: {
    marginTop: "48px",
    maxWidth: "660px",
  },
}));

interface Tab {
  slug: string;
  component?: React.FunctionComponent;
  onClick?: (options?: any) => void;
}

const constructProfileTabs: (
  type: "user" | "appUser" | "instructor",
  hasRequiredProduct: boolean
) => Tab[] = (type, hasRequiredProduct) => {
  return [
    {
      slug: "dashboard",
      component: ProfilePageDashboard,
    },
    {
      slug: "favourites",
      component: ProfilePageFavourites,
    },
    {
      slug: "account",
      component: ProfilePageAccount,
    },
    ...(hasRequiredProduct
      ? [
          {
            slug: "recruitments",
            component: ProfilePageRecruitments,
          },
        ]
      : []),
    {
      slug: "billing",
      component: ProfilePageBilling,
    },
  ];
};

const fontStyle = css`
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-decoration: none;
`;

const MenuItemLink = styled(Link)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  padding: inherit;
  font-size: 13px;
`;

const MenuItemButton = styled(KvixButton)`
  ${fontStyle};
`;

export const ProfileNavigation: React.FC<RouteChildrenProps> = (props) => {
  const classes = useStyles(props);
  const location = useLocation();
  const { user } = useContext(KvixUserContext);
  const { isNativeApp } = useContext(ExpoContext);
  const { t, ready } = useTranslation(["account", "pages"]);
  const history = useHistory();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const compact = useMediaQuery<Theme>((theme) => theme.breakpoints.down(700));

  const tabs = constructProfileTabs(
    isNativeApp ? "appUser" : isInstructor(user) ? "instructor" : "user",
    hasRequiredProduct(user, StripeProduct.LIVE)
  );

  const activeTab = tabs.find((tab) => {
    return location.pathname === `${props.match.path}/${tab.slug}`;
  });

  if (!ready) {
    return null;
  }

  if (!activeTab) {
    return <Redirect to={`${props.match.path}/${tabs[0].slug}`} />;
  }

  return (
    <Grid container direction="column" alignItems="center">
      <Grid item xs={12}>
        {compact ? (
          <>
            <Button
              onClick={handleClick}
              aria-controls="simple-menu"
              aria-haspopup="true"
              variant="contained"
              color="primary"
            >
              {t(`pages:profile.${activeTab.slug}.title`)}
              {!!anchorEl ? <KvixIcon.Chevron.Up /> : <KvixIcon.Chevron.Down />}
            </Button>
            <Menu
              open={Boolean(anchorEl)}
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              onClose={handleClose}
            >
              {tabs.map((tab) => (
                <MenuItem
                  onClick={() => {
                    tab.onClick && tab.onClick({ history });
                    handleClose();
                  }}
                  key={tab.slug}
                  value={tab.slug}
                  style={{ width: "150px" }}
                >
                  <MenuItemLink to={`${props.match.path}/${tab.slug}`}>
                    {t(`pages:profile.${tab.slug}.title`)}
                  </MenuItemLink>
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Grid container spacing={1} className={classes.tabContainer}>
            {tabs.map((tab) => (
              <Grid item key={tab.slug}>
                <NavLink exact to={`${props.match.path}/${tab.slug}`}>
                  <MenuItemButton
                    variant="text"
                    color="primary"
                    lowercase
                    style={{
                      color:
                        activeTab === tab
                          ? theme.palette.primary.main
                          : "#B1B2B3",
                    }}
                  >
                    {t(`pages:profile.${tab.slug}.title`)}
                  </MenuItemButton>
                </NavLink>
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>
      <Grid
        container
        item
        xs={12}
        className={classes.tabContent}
        justify="center"
      >
        <Switch>
          {tabs.map(
            (tab) =>
              tab.component && (
                <Route
                  key={tab.slug}
                  path={`${props.match.path}/${tab.slug}`}
                  component={tab.component}
                />
              )
          )}
        </Switch>
      </Grid>
    </Grid>
  );
};
